import React, { useState, useEffect, useRef } from 'react';
import { FileSpreadsheet, Upload, TrendingUp, Users, BarChart3, RefreshCw, History, Search, FileDown , Download, Calculator} from 'lucide-react';
import axios from 'axios';
import { retentionLabel, retentionConversion } from '../components/retentionLabels';
import { toast } from 'sonner';
import OverlayTab from './OverlayTab';
import PPSTOriginalTotals from '../components/PPSTOriginalTotals';
import LastAutomaticImport from '../components/LastAutomaticImport';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SuperUnion() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('totales'); // 'totales', 'ppsr', 'ppst', 'overlay', 'historico'
  const [snapshots, setSnapshots] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const userStored = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = userStored.role !== 'worker';

  const [isUploading, setIsUploading] = useState(false);
  const [uploadType, setUploadType] = useState(''); // 'PPST' o 'PPSR'
  const [lastUpdatePPSR, setLastUpdatePPSR] = useState(null);
  const [lastUpdatePPST, setLastUpdatePPST] = useState(null);
  const [lastUpdateMethodPPSR, setLastUpdateMethodPPSR] = useState(null);
  const [lastUpdateMethodPPST, setLastUpdateMethodPPST] = useState(null);
  const [showRealValues, setShowRealValues] = useState(false); // false = mostrar Excel (1:6), true = dividir por 6 (1:1)
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [acumuladoPPSR, setAcumuladoPPSR] = useState({
    ganancia_total: 0,
    de_oponentes: 0,
    del_jackpot: 0,
    ganancias_union: 0,
    cuota: 0,
    cuota_jackpot: 0,
    premio_jackpot: 0
  });
  
  const [tablaTotalPPSR, setTablaTotalPPSR] = useState([]);
  const [totalPPSRRow, setTotalPPSRRow] = useState(null);
  
  const [tablaTotalPPST, setTablaTotalPPST] = useState([]);
  const [totalPPSTRow, setTotalPPSTRow] = useState(null);
  
  // Estados para vistas de consulta
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [uniones, setUniones] = useState([]);
  const [selectedUnion, setSelectedUnion] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [jugadores, setJugadores] = useState([]);
  const [tipoBusquedaComb, setTipoBusquedaComb] = useState('club');
  const [busquedaClubComb, setBusquedaClubComb] = useState('');
  const [busquedaUnionComb, setBusquedaUnionComb] = useState('');
  const [fechaDesdeComb, setFechaDesdeComb] = useState('');
  const [fechaHastaComb, setFechaHastaComb] = useState('');
  const [resultadoComb, setResultadoComb] = useState(null);
  const [loadingComb, setLoadingComb] = useState(false);
  // Sugerencias (búsqueda flexible por nombre/id)
  const [sugerencias, setSugerencias] = useState([]);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const [loadingSugerencias, setLoadingSugerencias] = useState(false);
  const sugerenciasTimerRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadSnapshots();
  }, []);

  const calcularAcumuladoPPSR = (ppsrSnapshots) => {
    // Filtrar por fechas si están definidas
    let snapshotsFiltered = ppsrSnapshots;
    
    if (dateFrom || dateTo) {
      snapshotsFiltered = ppsrSnapshots.filter(s => {
        const fechaSnapshot = s.fecha; // Usar campo 'fecha' en lugar de 'fecha_importacion'
        if (dateFrom && fechaSnapshot < dateFrom) return false;
        if (dateTo && fechaSnapshot > dateTo) return false;
        return true;
      });
    }
    
    // Acumular totales
    const acumulado = {
      ganancia_total: 0,
      de_oponentes: 0,
      del_jackpot: 0,
      ganancias_union: 0,
      cuota: 0,
      cuota_jackpot: 0,
      premio_jackpot: 0
    };
    
    snapshotsFiltered.forEach(snapshot => {
      if (snapshot.totales_ppsr) {
        acumulado.ganancia_total += snapshot.totales_ppsr.ganancia_total || 0;
        acumulado.de_oponentes += snapshot.totales_ppsr.de_oponentes || 0;
        acumulado.del_jackpot += snapshot.totales_ppsr.del_jackpot || 0;
        acumulado.ganancias_union += snapshot.totales_ppsr.ganancias_union || 0;
        acumulado.cuota += snapshot.totales_ppsr.cuota || 0;
        acumulado.cuota_jackpot += snapshot.totales_ppsr.cuota_jackpot || 0;
        acumulado.premio_jackpot += snapshot.totales_ppsr.premio_jackpot || 0;
      }
    });
    
    setAcumuladoPPSR(acumulado);
  };

  const calcularTablaTotalPPSR = (ppsrSnapshots) => {
    // Filtrar por fechas si están definidas
    let snapshotsFiltered = ppsrSnapshots;
    
    if (dateFrom || dateTo) {
      snapshotsFiltered = ppsrSnapshots.filter(s => {
        const fechaSnapshot = s.fecha; // Usar campo 'fecha' en lugar de 'fecha_importacion'
        if (dateFrom && fechaSnapshot < dateFrom) return false;
        if (dateTo && fechaSnapshot > dateTo) return false;
        return true;
      });
    }
    
    // Agrupar por union_id y sumar valores
    const unionesMap = {};
    
    snapshotsFiltered.forEach(snapshot => {
      if (snapshot.totales_ppsr && snapshot.totales_ppsr.uniones) {
        snapshot.totales_ppsr.uniones.forEach(union => {
          const unionId = union.union_id;
          
          if (!unionesMap[unionId]) {
            unionesMap[unionId] = {
              row_index: union.row_index || 0,  // Guardar índice para orden
              nombre_union: union.nombre_union,
              union_id: unionId,
              clasificacion_ppsr: union.clasificacion_ppsr,
              ganancia_total: 0,
              ganancia_de_oponentes: 0,
              ganancia_del_jackpot: 0,
              ganancia_del_dividir_ev: 0,
              ganancias_union_total: 0,
              ganancias_union_cuota: 0,
              ganancias_union_cuota_jackpot: 0,
              ganancias_union_premio_jackpot: 0,
              ganancias_union_dividir_ev: 0
            };
          }
          
          // Sumar valores
          unionesMap[unionId].ganancia_de_oponentes += union.ganancia_de_oponentes || 0;
          unionesMap[unionId].ganancia_del_jackpot += union.ganancia_del_jackpot || 0;
          unionesMap[unionId].ganancia_del_dividir_ev += union.ganancia_del_dividir_ev || 0;
          unionesMap[unionId].ganancias_union_cuota += union.ganancias_union_cuota || 0;
          unionesMap[unionId].ganancias_union_cuota_jackpot += union.ganancias_union_cuota_jackpot || 0;
          unionesMap[unionId].ganancias_union_premio_jackpot += union.ganancias_union_premio_jackpot || 0;
          unionesMap[unionId].ganancias_union_dividir_ev += union.ganancias_union_dividir_ev || 0;
          
          // CALCULAR totales (F = G+H+I, J = K+L+M+N)
          unionesMap[unionId].ganancia_total = 
            unionesMap[unionId].ganancia_de_oponentes + 
            unionesMap[unionId].ganancia_del_jackpot + 
            unionesMap[unionId].ganancia_del_dividir_ev;
          
          unionesMap[unionId].ganancias_union_total = 
            unionesMap[unionId].ganancias_union_cuota + 
            unionesMap[unionId].ganancias_union_cuota_jackpot + 
            unionesMap[unionId].ganancias_union_premio_jackpot + 
            unionesMap[unionId].ganancias_union_dividir_ev;
        });
      }
    });
    
    // Convertir a array y ORDENAR por row_index (orden original del Excel)
    const unionesArray = Object.values(unionesMap).sort((a, b) => a.row_index - b.row_index);
    
    // Calcular fila Total (suma de todas las filas)
    const totalRow = {
      nombre_union: 'Total',
      union_id: 'TOTAL',
      clasificacion_ppsr: 0,
      ganancia_total: unionesArray.reduce((sum, u) => sum + u.ganancia_total, 0),
      ganancia_de_oponentes: unionesArray.reduce((sum, u) => sum + u.ganancia_de_oponentes, 0),
      ganancia_del_jackpot: unionesArray.reduce((sum, u) => sum + u.ganancia_del_jackpot, 0),
      ganancia_del_dividir_ev: unionesArray.reduce((sum, u) => sum + u.ganancia_del_dividir_ev, 0),
      ganancias_union_total: unionesArray.reduce((sum, u) => sum + u.ganancias_union_total, 0),
      ganancias_union_cuota: unionesArray.reduce((sum, u) => sum + u.ganancias_union_cuota, 0),
      ganancias_union_cuota_jackpot: unionesArray.reduce((sum, u) => sum + u.ganancias_union_cuota_jackpot, 0),
      ganancias_union_premio_jackpot: unionesArray.reduce((sum, u) => sum + u.ganancias_union_premio_jackpot, 0),
      ganancias_union_dividir_ev: unionesArray.reduce((sum, u) => sum + u.ganancias_union_dividir_ev, 0)
    };
    
    setTablaTotalPPSR(unionesArray);
    setTotalPPSRRow(totalRow);
  };


  const calcularTablaTotalPPST = (ppstSnapshots) => {
    // Filtrar por fechas si están definidas
    let snapshotsFiltered = ppstSnapshots;
    
    if (dateFrom || dateTo) {
      snapshotsFiltered = ppstSnapshots.filter(s => {
        const fechaSnapshot = s.fecha; // Usar campo 'fecha' en lugar de 'fecha_importacion'
        if (dateFrom && fechaSnapshot < dateFrom) return false;
        if (dateTo && fechaSnapshot > dateTo) return false;
        return true;
      });
    }
    
    // PASO 1: Agrupar snapshots por día y tomar solo el más reciente de cada día
    const snapshotsPorDia = {};
    
    snapshotsFiltered.forEach(snapshot => {
      // Extraer solo la fecha (sin hora) para agrupar por día
      const fechaDia = snapshot.fecha; // Ya es string en formato YYYY-MM-DD
      
      if (!snapshotsPorDia[fechaDia] || 
          new Date(snapshot.fecha_importacion) > new Date(snapshotsPorDia[fechaDia].fecha_importacion)) {
        // Si no existe snapshot para este día, o este es más reciente, guardarlo
        snapshotsPorDia[fechaDia] = snapshot;
      }
    });
    
    // Convertir a array los snapshots únicos por día
    const snapshotsUnicos = Object.values(snapshotsPorDia);
    
    // PASO 2: Agrupar por union_id y sumar valores
    const unionesMap = {};
    let deficitGarantizadoAcumulado = 0;
    
    snapshotsUnicos.forEach(snapshot => {
      if (snapshot.totales_ppst && snapshot.totales_ppst.uniones) {
        // Déficit garantizado: sumar solo entre días diferentes
        // Extraer del objeto totales_ppst directamente (deficit_garantizado_snapshot)
        // o de la primera unión (fallback para datos existentes)
        const deficitSnapshot = snapshot.totales_ppst.deficit_garantizado_snapshot 
          || (snapshot.totales_ppst.uniones.length > 0 
              ? (snapshot.totales_ppst.uniones[0].ganancias_union_deficit_garantizado || 0)
              : 0);
        deficitGarantizadoAcumulado += deficitSnapshot;
        
        snapshot.totales_ppst.uniones.forEach(union => {
          const unionId = union.union_id;
          
          if (!unionesMap[unionId]) {
            unionesMap[unionId] = {
              row_index: union.row_index || 0,
              nombre_union: union.nombre_union,
              union_id: unionId,
              ganancia: 0,
              valor_ganado_ticket: 0,
              entrada_ticket: 0,
              valor_premio_personalizado: 0,
              ganancias_union_total: 0,
              ganancias_union_cuota: 0,
              ganancias_union_entrada_spinup: 0,
              ganancias_union_premio_spinup: 0,
              ganancias_union_valor_emitido_ticket: 0,
              ganancias_union_entrada_ticket: 0
              // NO incluir ganancias_union_deficit_garantizado en filas individuales
            };
          }
          
          // Sumar valores
          unionesMap[unionId].ganancia += union.ganancia || 0;
          unionesMap[unionId].valor_ganado_ticket += union.valor_ganado_ticket || 0;
          unionesMap[unionId].entrada_ticket += union.entrada_ticket || 0;
          unionesMap[unionId].valor_premio_personalizado += union.valor_premio_personalizado || 0;
          unionesMap[unionId].ganancias_union_cuota += union.ganancias_union_cuota || 0;
          unionesMap[unionId].ganancias_union_entrada_spinup += union.ganancias_union_entrada_spinup || 0;
          unionesMap[unionId].ganancias_union_premio_spinup += union.ganancias_union_premio_spinup || 0;
          unionesMap[unionId].ganancias_union_valor_emitido_ticket += union.ganancias_union_valor_emitido_ticket || 0;
          unionesMap[unionId].ganancias_union_entrada_ticket += union.ganancias_union_entrada_ticket || 0;
          
          // CALCULAR total ganancias unión (H = I+J+K+L+M) - SIN déficit garantizado
          unionesMap[unionId].ganancias_union_total = 
            unionesMap[unionId].ganancias_union_cuota + 
            unionesMap[unionId].ganancias_union_entrada_spinup + 
            unionesMap[unionId].ganancias_union_premio_spinup + 
            unionesMap[unionId].ganancias_union_valor_emitido_ticket + 
            unionesMap[unionId].ganancias_union_entrada_ticket;
        });
      }
    });
    
    // Convertir a array y ORDENAR por row_index (orden original del Excel)
    const unionesArray = Object.values(unionesMap).sort((a, b) => a.row_index - b.row_index);
    
    // PASO 3: Calcular fila Total
    // Déficit garantizado SOLO se muestra en fila total (valor acumulado entre días diferentes)
    const totalRow = {
      nombre_union: 'Total',
      union_id: 'TOTAL',
      ganancia: unionesArray.reduce((sum, u) => sum + u.ganancia, 0),
      valor_ganado_ticket: unionesArray.reduce((sum, u) => sum + u.valor_ganado_ticket, 0),
      entrada_ticket: unionesArray.reduce((sum, u) => sum + u.entrada_ticket, 0),
      valor_premio_personalizado: unionesArray.reduce((sum, u) => sum + u.valor_premio_personalizado, 0),
      ganancias_union_total: unionesArray.reduce((sum, u) => sum + u.ganancias_union_total, 0),
      ganancias_union_cuota: unionesArray.reduce((sum, u) => sum + u.ganancias_union_cuota, 0),
      ganancias_union_entrada_spinup: unionesArray.reduce((sum, u) => sum + u.ganancias_union_entrada_spinup, 0),
      ganancias_union_premio_spinup: unionesArray.reduce((sum, u) => sum + u.ganancias_union_premio_spinup, 0),
      ganancias_union_valor_emitido_ticket: unionesArray.reduce((sum, u) => sum + u.ganancias_union_valor_emitido_ticket, 0),
      ganancias_union_entrada_ticket: unionesArray.reduce((sum, u) => sum + u.ganancias_union_entrada_ticket, 0),
      ganancias_union_deficit_garantizado: deficitGarantizadoAcumulado  // Solo en fila total
    };
    
    setTablaTotalPPST(unionesArray);
    setTotalPPSTRow(totalRow);
  };


  // Recalcular cuando cambian las fechas
  useEffect(() => {
    const ppsrSnapshots = snapshots.filter(s => s.tipo === 'PPSR');
    const ppstSnapshots = snapshots.filter(s => s.tipo === 'PPST');
    calcularAcumuladoPPSR(ppsrSnapshots);
    calcularTablaTotalPPSR(ppsrSnapshots);
    calcularTablaTotalPPST(ppstSnapshots);
  }, [dateFrom, dateTo, snapshots]);

  const loadSnapshots = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superunion/snapshots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const snaps = response.data.snapshots || [];
      setSnapshots(snaps);
      
      // Calcular última actualización por tipo
      const ppsrSnapshots = snaps.filter(s => s.tipo === 'PPSR');
      const ppstSnapshots = snaps.filter(s => s.tipo === 'PPST');
      
      if (ppsrSnapshots.length > 0) {
        // Ordenar por fecha de importación y tomar el más reciente
        const lastPPSR = ppsrSnapshots.sort((a, b) => 
          new Date(b.fecha_importacion) - new Date(a.fecha_importacion)
        )[0];
        setLastUpdatePPSR(lastPPSR.fecha_importacion);
      }
      
      if (ppstSnapshots.length > 0) {
        const lastPPST = ppstSnapshots.sort((a, b) => 
          new Date(b.fecha_importacion) - new Date(a.fecha_importacion)
        )[0];
        setLastUpdatePPST(lastPPST.fecha_importacion);
      }
      
      // Obtener información del método de carga desde el log
      try {
        const logsResponse = await axios.get(`${API}/uploads/last-update`);
        if (logsResponse.data) {
          // Setear método de PPST si existe
          if (logsResponse.data.last_ppst) {
            setLastUpdateMethodPPST(logsResponse.data.last_ppst.upload_method_label);
          }
          // Setear método de PPSR si existe
          if (logsResponse.data.last_ppsr) {
            setLastUpdateMethodPPSR(logsResponse.data.last_ppsr.upload_method_label);
          }
        }
      } catch (logError) {
        console.log('No se pudo obtener información de método de carga:', logError);
      }
      
      // Calcular acumulado PPSR (inicialmente sin filtro de fechas)
      calcularAcumuladoPPSR(ppsrSnapshots);
      
    } catch (error) {
      console.error('Error cargando snapshots:', error);
      toast.error('Error al cargar histórico');
    } finally {
      setLoading(false);
    }
  };

  // Upload unificado: N archivos (ej: los 14 de la semana, PPST y PPSR mezclados).
  // Se manda UN archivo por llamada: el backend autodetecta el tipo y saca la fecha
  // del CONTENIDO del Excel, asi que cada archivo cae en su propia fecha.
  const handleFileUploadUnified = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadType('UNIFIED');

    const token = localStorage.getItem('token');
    const resultados = [];   // {tipo, fecha, archivo, detalle}
    const fallidos = [];     // {archivo, motivo}
    const warnings = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setUploadStage(`Procesando ${i + 1}/${files.length}: ${f.name}`);
        setUploadProgress(Math.round((i / files.length) * 100));

        const formData = new FormData();
        formData.append('files', f);

        try {
          const response = await axios.post(`${API}/superunion/import-unified`, formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
            timeout: 600000,
          });
          const data = response.data || {};
          if (data.PPST) {
            resultados.push({
              tipo: 'PPST', icon: '🏆', fecha: data.PPST.fecha, archivo: f.name,
              detalle: `${(data.PPST.ppst_partidas || 0).toLocaleString()} partidas · ${(data.PPST.torneos || 0).toLocaleString()} torneos`,
            });
          }
          if (data.PPSR) {
            resultados.push({
              tipo: 'PPSR', icon: '💰', fecha: data.PPSR.fecha, archivo: f.name,
              detalle: `${(data.PPSR.ppsr_partidas || 0).toLocaleString()} partidas · ${data.PPSR.ppsr_clubs || 0} clubs`,
            });
          }
          if (data.warnings?.length) warnings.push(...data.warnings);
          if (!data.PPST && !data.PPSR) fallidos.push({ archivo: f.name, motivo: 'no se detecto PPST ni PPSR' });
        } catch (err) {
          console.error(`Error importando ${f.name}:`, err);
          fallidos.push({ archivo: f.name, motivo: err.response?.data?.detail || err.message || 'error desconocido' });
        }
      }

      setUploadProgress(100);
      setUploadStage('¡Completado!');

      const nPPST = resultados.filter(r => r.tipo === 'PPST').length;
      const nPPSR = resultados.filter(r => r.tipo === 'PPSR').length;
      const fechas = [...new Set(resultados.map(r => r.fecha))].sort();

      toast(
        (id) => (
          <div className="flex flex-col gap-3 min-w-[340px]">
            <div className="flex items-center gap-2 font-bold text-base">
              <span>{fallidos.length === 0 ? '✅' : '⚠️'}</span>
              <span>{files.length} archivo(s) · {nPPST} PPST + {nPPSR} PPSR</span>
            </div>
            {fechas.length > 0 && (
              <div className="text-xs text-gray-600">
                📅 Fechas: {fechas.length === 1 ? fechas[0] : `${fechas[0]} → ${fechas[fechas.length - 1]}`} ({fechas.length} día/s)
              </div>
            )}
            <div className="max-h-72 overflow-y-auto flex flex-col gap-1">
              {resultados.map((r, idx) => (
                <div key={idx} className="text-xs border-l-4 border-blue-500 pl-2 py-1 bg-blue-50 rounded">
                  <span className="font-semibold">{r.icon} {r.tipo} {r.fecha}</span>
                  <span className="text-gray-600"> — {r.detalle}</span>
                </div>
              ))}
              {fallidos.map((f, idx) => (
                <div key={`e${idx}`} className="text-xs border-l-4 border-red-500 pl-2 py-1 bg-red-50 rounded">
                  <span className="font-semibold">❌ {f.archivo}</span>
                  <span className="text-gray-600"> — {f.motivo}</span>
                </div>
              ))}
              {warnings.map((w, idx) => (
                <div key={`w${idx}`} className="text-xs text-yellow-700 bg-yellow-50 rounded px-2 py-1">⚠️ {w}</div>
              ))}
            </div>
            <button
              onClick={() => toast.dismiss(id)}
              data-testid="import-toast-ok-btn"
              className="self-end mt-1 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded transition-colors"
            >
              OK
            </button>
          </div>
        ),
        { duration: Infinity, closeButton: false }
      );

      await loadSnapshots();
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStage('');
      setUploadType('');
      if (e.target) e.target.value = '';
    }
  };

  // (LEGACY) Upload separado - se mantiene por compatibilidad
  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Por favor selecciona un archivo Excel (.xlsx)');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadType(type);
    setUploadStage(`Preparando archivo ${type}...`);

    try {
      const token = localStorage.getItem('token');
      
      // Dividir archivo en chunks de 2MB
      const chunkSize = 2 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / chunkSize);
      const uploadId = file.name.replace('.xlsx', '').replace('.xls', '');

      // Subir chunks (0-20%)
      setUploadStage(`Subiendo archivo ${type} en partes...`);
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);
        
        const base64Chunk = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.readAsDataURL(chunk);
        });

        await axios.post(`${API}/superunion/upload-chunk`, {
          upload_id: uploadId,
          chunk_number: i + 1,
          total_chunks: totalChunks,
          chunk_data: base64Chunk,
          file_hash: 'hash'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setUploadProgress(Math.round(((i + 1) / totalChunks) * 20));
      }

      // Procesar con endpoint específico
      setUploadProgress(30);
      setUploadStage(`Procesando Total ${type} (Ratio 1:6)...`);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setUploadProgress(50);
      setUploadStage(`Procesando Partidas ${type}...`);
      
      const endpoint = type === 'PPST' ? 'process-ppst' : 'process-ppsr';
      const processResponse = await axios.post(`${API}/superunion/${endpoint}?upload_id=${uploadId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUploadProgress(90);
      setUploadStage('Guardando en base de datos...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setUploadProgress(100);
      setUploadStage('¡Completado!');
      
      const partidasCount = type === 'PPST' 
        ? processResponse.data.stats.ppst_partidas 
        : processResponse.data.stats.ppsr_partidas;
      
      toast.success(`✅ Archivo ${type} procesado: ${partidasCount} partidas`);
      
      await loadSnapshots();
      
    } catch (error) {
      console.error('Error subiendo archivo:', error);
      toast.error('Error al procesar archivo: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStage('');
      setUploadType('');
    }
  };

  // Función para extraer IDs unificados (PPSR + PPST)
  const handleExtraerIDsUnificados = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API}/control-bot/ganadores-nuevos-unificado`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      
      if (!data.success) {
        toast.error('Error obteniendo IDs');
        return;
      }
      
      if (data.ids_nuevos.length === 0) {
        toast.info('No hay IDs nuevos para exportar');
        return;
      }
      
      // Crear Excel con los IDs
      const XLSX = await import('xlsx');
      const ws_data = [['jugador_id']];
      data.ids_nuevos.forEach(id => ws_data.push([id]));
      
      const ws = XLSX.utils.aoa_to_sheet(ws_data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'IDs');
      
      // Descargar Excel
      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `ids_control_bot_unificado_${fecha}.xlsx`);
      
      // Mostrar mensaje con estadísticas
      const ppsr_msg = `PPSR: ${data.ppsr.nuevos} nuevos`;
      const ppst_msg = `PPST: ${data.ppst.nuevos} nuevos`;
      
      const rango_msg = data.hasta_fecha
        ? `semana vencida ${data.desde_fecha} al ${data.hasta_fecha}`
        : `semana en curso desde ${data.desde_fecha}`;
      const restantes_msg = data.restantes > 0 ? ` - Quedan ${data.restantes} pendientes` : '';

      toast.success(`Descargado: ${data.total_exportando} IDs (${rango_msg}) - ${ppsr_msg} - ${ppst_msg}${restantes_msg}`, { duration: 8000 });

      
      // ========== MARCAR IDS COMO ENVIADOS ==========
      // Después de descargar exitosamente, marcar automáticamente como enviados
      try {
        await axios.post(
          `${API}/control-bot/marcar-enviados-unificado`,
          { ids: data.ids_nuevos },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log(`✅ ${data.ids_nuevos.length} IDs marcados como enviados en historial`);
      } catch (markError) {
        console.error("Error marcando IDs como enviados:", markError);
        // No mostramos error al usuario, solo logueamos
        // El Excel ya se descargó exitosamente
      }
      
    } catch (error) {
      console.error('Error extrayendo IDs unificados:', error);
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ========== BOT RESULTADOS UNIFICADO ==========
  
  const [botResultadosFiles, setBotResultadosFiles] = useState([]);
  const [botResultadosFechaInicio, setBotResultadosFechaInicio] = useState('');
  const [botResultadosFechaFin, setBotResultadosFechaFin] = useState('');
  const [botResultadosIncluirPerdedores, setBotResultadosIncluirPerdedores] = useState(false);
  const [botResultadosProcesando, setBotResultadosProcesando] = useState(false);
  const [botResultadosResultado, setBotResultadosResultado] = useState(null);
  const [snapshotHistory, setSnapshotHistory] = useState([]);
  const [showSnapshotHistory, setShowSnapshotHistory] = useState(false);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);

  const cargarSnapshotHistory = async () => {
    setLoadingSnapshots(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API}/superunion/snapshots/history?tipo=PPST`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.data.success) setSnapshotHistory(resp.data.snapshots);
    } catch (err) {
      console.error('Error cargando historial:', err);
    } finally {
      setLoadingSnapshots(false);
    }
  };

  const [botResultadosHistorial, setBotResultadosHistorial] = useState([]);
  const [botResultadosMostrarHistorial, setBotResultadosMostrarHistorial] = useState(false);

  const handleBotResultadosFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const invalidos = files.filter(f => !f.name.endsWith('.xlsx') && !f.name.endsWith('.xls'));
    if (invalidos.length > 0) {
      toast.error('Todos los archivos deben ser Excel (.xlsx/.xls)');
      return;
    }
    setBotResultadosFiles(files);
    toast.success(`${files.length} archivo(s) seleccionado(s)`);
  };

  const cargarBotResultadosHistorial = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/bot-resultados-unificado/historial`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBotResultadosHistorial(response.data);
    } catch (error) {
      console.error('Error cargando historial:', error);
    }
  };

  const handleProcesarBotResultados = async () => {
    if (!botResultadosFiles || botResultadosFiles.length === 0) {
      toast.error('Por favor selecciona al menos un archivo Excel');
      return;
    }

    if (!botResultadosFechaInicio || !botResultadosFechaFin) {
      toast.error('Por favor selecciona las fechas de inicio y fin');
      return;
    }

    try {
      setBotResultadosProcesando(true);
      const token = localStorage.getItem('token');

      const formData = new FormData();
      botResultadosFiles.forEach(f => formData.append('files', f));
      formData.append('semana_inicio', botResultadosFechaInicio);
      formData.append('semana_fin', botResultadosFechaFin);
      formData.append('incluir_perdedores', botResultadosIncluirPerdedores);

      const response = await axios.post(
        `${API}/bot-resultados-unificado/procesar`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setBotResultadosResultado(response.data);
        toast.success(response.data.message);
        
        // Recargar historial
        await cargarBotResultadosHistorial();
      } else {
        toast.error(response.data.message || 'Error procesando archivo');
      }

    } catch (error) {
      console.error('Error procesando bot resultados:', error);
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBotResultadosProcesando(false);
    }
  };

  const handleExportarTotales = async (data) => {
    try {
      const archivos = data?.archivos || [];
      if (archivos.length === 0) {
        toast.error('No hay totales para exportar');
        return;
      }
      const conversion = retentionConversion(data);
      const XLSX = await import('xlsx');
      const rows = [['ID UNIÓN', retentionLabel(data), `USD (${conversion})`]];
      archivos.forEach(a => rows.push([a.union, Number(a.retencion || 0), Number(a.usd || 0)]));
      rows.push(['TOTAL', Number(data.retencion_total || 0), Number(data.usd_total || 0)]);
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 16 }, { wch: 16 }, { wch: 16 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Totales');
      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `resultados_totales_bot_${fecha}.xlsx`);
      toast.success('Excel de totales descargado');
    } catch (e) {
      console.error('Error exportando totales:', e);
      toast.error('No se pudo exportar los totales');
    }
  };

  // Copiar la tabla de totales al portapapeles (formato TSV, pega en Excel/Sheets)
  const handleCopiarTotales = async (data) => {
    try {
      const archivos = data?.archivos || [];
      if (archivos.length === 0) {
        toast.error('No hay totales para copiar');
        return;
      }
      const conversion = retentionConversion(data);
      const lineas = [`ID UNIÓN\t${retentionLabel(data)}\tUSD (${conversion})`];
      archivos.forEach(a => lineas.push(`${a.union}\t${Number(a.retencion || 0).toFixed(2)}\t${Number(a.usd || 0).toFixed(2)}`));
      lineas.push(`TOTAL\t${Number(data.retencion_total || 0).toFixed(2)}\t${Number(data.usd_total || 0).toFixed(2)}`);
      await navigator.clipboard.writeText(lineas.join('\n'));
      toast.success('Totales copiados al portapapeles');
    } catch (e) {
      console.error('Error copiando totales:', e);
      toast.error('No se pudo copiar (revisá permisos del navegador)');
    }
  };


  // Exportar la tabla de totales como IMAGEN (PNG limpio, sin metadatos/EXIF).
  // Se dibuja a mano en un canvas: no lleva marcas ni datos ocultos.
  const handleExportarImagen = (data) => {
    try {
      const archivos = data?.archivos || [];
      if (archivos.length === 0) {
        toast.error('No hay totales para exportar');
        return;
      }
      const fmt = (n) => Number(n || 0).toFixed(2);

      // Layout (en px lógicos; se escala x3 para nitidez)
      const SCALE = 3;
      const COL_W = [170, 160, 180];
      const HEADER_H = 48;
      const ROW_H = 42;
      const W = COL_W[0] + COL_W[1] + COL_W[2];
      const H = HEADER_H + ROW_H * archivos.length;

      const canvas = document.createElement('canvas');
      canvas.width = W * SCALE;
      canvas.height = H * SCALE;
      const ctx = canvas.getContext('2d');
      ctx.scale(SCALE, SCALE);
      ctx.textBaseline = 'middle';

      // Fondo blanco
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, W, H);

      const colX = [0, COL_W[0], COL_W[0] + COL_W[1]];
      const headers = ['ID UNION', retentionLabel(data), 'USD'];

      // Header amarillo
      ctx.fillStyle = '#FFEB00';
      ctx.fillRect(0, 0, W, HEADER_H);
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 20px Arial, sans-serif';
      ctx.textAlign = 'center';
      headers.forEach((h, i) => {
        ctx.fillText(h, colX[i] + COL_W[i] / 2, HEADER_H / 2);
      });

      // Filas
      archivos.forEach((a, r) => {
        const y = HEADER_H + r * ROW_H;
        // ID UNION (bold centrado)
        ctx.font = 'bold 20px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#000000';
        ctx.fillText(String(a.union), colX[0] + COL_W[0] / 2, y + ROW_H / 2);
        // RETENCION (derecha)
        ctx.font = '20px Arial, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(fmt(a.retencion), colX[1] + COL_W[1] - 14, y + ROW_H / 2);
        // USD (bold derecha)
        ctx.font = 'bold 20px Arial, sans-serif';
        ctx.fillText(fmt(a.usd), colX[2] + COL_W[2] - 14, y + ROW_H / 2);
      });

      // Grilla negra
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, W - 2, H - 2);
      // Líneas horizontales
      for (let r = 0; r <= archivos.length; r++) {
        const y = HEADER_H + r * ROW_H;
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }
      ctx.beginPath(); ctx.moveTo(0, HEADER_H); ctx.lineTo(W, HEADER_H); ctx.stroke();
      // Líneas verticales
      [colX[1], colX[2]].forEach(x => {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      });

      canvas.toBlob((blob) => {
        if (!blob) { toast.error('No se pudo generar la imagen'); return; }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const fecha = new Date().toISOString().split('T')[0];
        link.href = url;
        link.download = `retenciones_${fecha}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Imagen descargada (sin metadatos)');
      }, 'image/png');
    } catch (e) {
      console.error('Error exportando imagen:', e);
      toast.error('No se pudo generar la imagen');
    }
  };

  const handleDescargarIdsFiltrados = async (historialId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/bot-resultados-unificado/ids-filtrados/${historialId}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ids_filtrados_${historialId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Semana filtrada descargada (ID + Respuesta)');
    } catch (error) {
      console.error('Error descargando IDs filtrados:', error);
      toast.error(error.response?.status === 404 ? 'Este análisis no tiene IDs filtrados guardados' : 'Error descargando la semana filtrada');
    }
  };

  const handleDescargarBotResultado = async (historialId, union) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/bot-resultados-unificado/descargar/${historialId}/${union}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `informe_bot_${union}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(`Excel descargado: ${union}`);
    } catch (error) {
      console.error('Error descargando:', error);
      toast.error('Error descargando archivo');
    }
  };

  // Cargar historial al montar
  useEffect(() => {
    cargarBotResultadosHistorial();
  }, []);



  const buscarCombinado = async (overrideId) => {
    const id = (overrideId != null ? String(overrideId) : (tipoBusquedaComb === 'club' ? busquedaClubComb : busquedaUnionComb)).trim();
    if (!id) { toast.error('Ingrese un ID o nombre'); return; }
    setLoadingComb(true);
    setResultadoComb(null);
    setMostrarSugerencias(false);
    try {
      const token = localStorage.getItem('token');
      const params = tipoBusquedaComb === 'club' ? { club_id: id } : { union_id: id };
      if (fechaDesdeComb) params.fecha_desde = fechaDesdeComb;
      if (fechaHastaComb) params.fecha_hasta = fechaHastaComb;
      const endpoint = tipoBusquedaComb === 'club' ? 'buscar-club' : 'buscar-union';
      const res = await axios.get(`${API}/superunion/combinado/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      if (res.data.success) {
        setResultadoComb(res.data);
        toast.success(`Encontrado: PPSR ${res.data.ppsr.jugadores_activos} jug + PPST ${res.data.ppst.jugadores_activos} jug`);
      }
    } catch (e) { toast.error('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setLoadingComb(false); }
  };

  const fetchSugerencias = (query) => {
    if (sugerenciasTimerRef.current) clearTimeout(sugerenciasTimerRef.current);
    const q = (query || '').trim();
    if (q.length < 1) {
      setSugerencias([]);
      setMostrarSugerencias(false);
      return;
    }
    sugerenciasTimerRef.current = setTimeout(async () => {
      setLoadingSugerencias(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/superunion/combinado/sugerir`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { tipo: tipoBusquedaComb, query: q, limit: 8 }
        });
        if (res.data.success) {
          setSugerencias(res.data.sugerencias || []);
          setMostrarSugerencias(true);
        }
      } catch (e) { /* silencioso, es autocompletado */ }
      finally { setLoadingSugerencias(false); }
    }, 300);
  };

  const seleccionarSugerencia = (sug) => {
    if (tipoBusquedaComb === 'club') setBusquedaClubComb(sug.id);
    else setBusquedaUnionComb(sug.id);
    setMostrarSugerencias(false);
    setSugerencias([]);
    buscarCombinado(sug.id);
  };

  const exportarCombinado = async () => {
    const id = tipoBusquedaComb === 'club' ? busquedaClubComb.trim() : busquedaUnionComb.trim();
    try {
      const token = localStorage.getItem('token');
      const params = { tipo: tipoBusquedaComb, id_valor: id };
      if (fechaDesdeComb) params.fecha_desde = fechaDesdeComb;
      if (fechaHastaComb) params.fecha_hasta = fechaHastaComb;
      const res = await axios.get(`${API}/superunion/combinado/exportar`, {
        headers: { Authorization: `Bearer ${token}` }, params, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `combinado_${tipoBusquedaComb}_${id}.xlsx`);
      document.body.appendChild(link);
      link.click(); link.remove();
      toast.success('Excel combinado descargado');
    } catch (e) { toast.error('Error al exportar'); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-violet-600" />
            Super Unión PPPoker
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Sistema multi-nivel: Super Unión → Unión → Club → Jugador
          </p>
        </div>

        {/* Botón unificado de importación (auto-detecta PPST/PPSR) */}
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (<label className="cursor-pointer">
            <div className="inline-flex h-9 items-center gap-2 px-3 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors">
              <Upload className="w-4 h-4" />
              Importar Excel
            </div>            <input
              type="file"
              accept=".xlsx,.xls"
              multiple
              onChange={handleFileUploadUnified}
              disabled={isUploading}
              className="hidden"
              data-testid="superunion-import-unified-input"
            />
          </label>)}

          {/* Botón Control Bot Unificado - Solo admin */}
          {isAdmin && (<button
            onClick={handleExtraerIDsUnificados}
            disabled={loading || isUploading}
            className="inline-flex h-9 items-center gap-2 px-3 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors"
          >
            <FileDown className="w-4 h-4" />
            Control bot
          </button>)}
        </div>
      </div>

        {/* Análisis Bot Resultados Unificado - Solo admin */}
        {isAdmin && (<div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="text-base font-semibold flex items-center gap-2">
              Análisis de resultados del bot
            </h2>
            <button
              onClick={() => setBotResultadosMostrarHistorial(!botResultadosMostrarHistorial)}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              {botResultadosMostrarHistorial ? 'Ocultar' : 'Ver'} Historial
            </button>
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Sube el Excel con respuestas de PPPoker (ID + Respuesta). El sistema buscará automáticamente en Cash Games (PPSR) y Torneos (PPST).
          </p>

          <div className="space-y-4">
            {/* Upload Excel */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Archivos de respuestas <span className="text-xs font-normal text-gray-500">(arrastrá todas las jornadas — el CRM autofiltra YES/BANNED)</span>
              </label>
              <input
                type="file"
                accept=".xlsx,.xls"
                multiple
                onChange={handleBotResultadosFileChange}
                disabled={botResultadosProcesando}
                data-testid="bot-resultados-file-input"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 dark:file:bg-purple-900 dark:file:text-purple-300"
              />
              {botResultadosFiles.length > 0 && (
                <div className="text-sm text-green-600 dark:text-green-400 mt-1" data-testid="bot-resultados-file-list">
                  ✅ {botResultadosFiles.length} archivo(s):
                  <ul className="list-disc list-inside text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    {botResultadosFiles.map((f, i) => (<li key={i}>{f.name}</li>))}
                  </ul>
                </div>
              )}
            </div>

            {/* Filtros de Fecha */}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Desde
                </label>
                <input
                  type="date" aria-label="Inicio del análisis bot"
                  value={botResultadosFechaInicio}
                  onChange={(e) => setBotResultadosFechaInicio(e.target.value)}
                  disabled={botResultadosProcesando}
                  className="h-9 w-[160px] max-w-full px-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Hasta
                </label>
                <input
                  type="date" aria-label="Fin del análisis bot"
                  value={botResultadosFechaFin}
                  onChange={(e) => setBotResultadosFechaFin(e.target.value)}
                  disabled={botResultadosProcesando}
                  className="h-9 w-[160px] max-w-full px-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>
            </div>

            {/* Checkbox Incluir Perdedores */}
            <div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={botResultadosIncluirPerdedores}
                  onChange={(e) => setBotResultadosIncluirPerdedores(e.target.checked)}
                  disabled={botResultadosProcesando}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <span className="text-gray-700 dark:text-gray-300">
                  Incluir jugadores con balance negativo
                </span>
              </label>
            </div>

            {/* Botón Procesar */}
            <button
              onClick={handleProcesarBotResultados}
              disabled={botResultadosProcesando || botResultadosFiles.length === 0}
              className="inline-flex h-9 items-center justify-center gap-2 px-4 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors font-medium focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            >
              {botResultadosProcesando ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  Procesar análisis
                </>
              )}
            </button>

            {/* Resultados */}
            {botResultadosResultado && (
              <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <h3 className="font-bold text-green-800 dark:text-green-300 mb-3">
                  ✅ Análisis Completado
                </h3>
                
                {/* Estadísticas */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                    <div className="text-2xl font-bold text-blue-600">{botResultadosResultado.stats?.ids_positivos || 0}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">IDs Positivos</div>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                    <div className="text-2xl font-bold text-green-600">{botResultadosResultado.stats?.jugadores_encontrados || 0}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Jugadores</div>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                    <div className="text-2xl font-bold text-purple-600">{botResultadosResultado.stats?.jugadores_mixtos || 0}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Mixtos</div>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                    <div className="text-2xl font-bold text-orange-600">{botResultadosResultado.stats?.uniones_afectadas || 0}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Uniones</div>
                  </div>
                </div>

                {/* Captura de Resultados Totales: ID Unión · Retención · USD */}
                <div className="mb-4" data-testid="captura-resultados-totales">
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <h4 className="font-medium text-gray-800 dark:text-gray-200">
                      🧾 Resultados Totales <span className="text-xs font-normal text-gray-500">(USD = {retentionConversion(botResultadosResultado)})</span>
                    </h4>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCopiarTotales(botResultadosResultado)}
                        className="px-3 py-1.5 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 rounded-lg transition-colors"
                        data-testid="copiar-totales-btn"
                      >
                        📋 Copiar
                      </button>
                      <button
                        onClick={() => handleExportarImagen(botResultadosResultado)}
                        className="px-3 py-1.5 text-xs bg-amber-400 hover:bg-amber-500 text-black rounded-lg transition-colors font-medium"
                        data-testid="exportar-imagen-btn"
                      >
                        🖼️ Imagen
                      </button>
                      <button
                        onClick={() => handleExportarTotales(botResultadosResultado)}
                        className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                        data-testid="exportar-totales-btn"
                      >
                        ⬇️ Excel
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                          <th className="text-left px-3 py-2 font-semibold">ID UNIÓN</th>
                          <th className="text-right px-3 py-2 font-semibold">{retentionLabel(botResultadosResultado)}</th>
                          <th className="text-right px-3 py-2 font-semibold">USD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {botResultadosResultado.archivos?.map((archivo, idx) => (
                          <tr key={idx} className="border-t border-gray-100 dark:border-gray-800" data-testid={`fila-total-${archivo.union}`}>
                            <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{archivo.union}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-800 dark:text-gray-200">
                              {Number(archivo.retencion || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400 font-medium">
                              ${Number(archivo.usd || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60 font-bold">
                          <td className="px-3 py-2 text-gray-900 dark:text-white">TOTAL</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-white" data-testid="retencion-total">
                            {Number(botResultadosResultado.retencion_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-emerald-700 dark:text-emerald-400" data-testid="usd-total">
                            ${Number(botResultadosResultado.usd_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Archivos por Unión */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h4 className="font-medium text-gray-800 dark:text-gray-200">
                      📊 Informes por Unión:
                    </h4>
                    {botResultadosResultado.historial_id && (
                      <button
                        onClick={() => handleDescargarIdsFiltrados(botResultadosResultado.historial_id)}
                        className="px-3 py-1.5 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                        data-testid="descargar-ids-filtrados-btn"
                        title="Descarga la semana filtrada (ID + Respuesta de todos los YES/BANNED)"
                      >
                        🗂️ Semana filtrada (IDs)
                      </button>
                    )}
                  </div>
                  {botResultadosResultado.archivos?.map((archivo, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {archivo.union}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {archivo.jugadores} jugadores • 
                          🎲 {archivo.cash_partidas} cash • 
                          🏆 {archivo.mtt_partidas} torneos
                          {archivo.mixtos > 0 && ` • 🎯 ${archivo.mixtos} mixtos`}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDescargarBotResultado(botResultadosResultado.historial_id, archivo.union)}
                        className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                      >
                        Descargar Excel
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Historial */}
            {botResultadosMostrarHistorial && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-3">
                  📜 Historial de Análisis
                </h3>
                
                {botResultadosHistorial.length === 0 ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No hay análisis anteriores
                  </p>
                ) : (
                  <div className="space-y-2">
                    {botResultadosHistorial.map((historial, idx) => (
                      <div key={idx} className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {new Date(historial.fecha_generacion).toLocaleString('es-AR')}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {historial.semana_inicio} al {historial.semana_fin} • 
                              {historial.jugadores_encontrados} jugadores ({historial.estadisticas?.jugadores_mixtos || 0} mixtos)
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              {historial.archivos?.length || 0} uniones • 
                              Por {historial.username}
                            </div>
                            {historial.retencion_total != null && (
                              <div className="mt-1 text-xs font-semibold text-gray-700 dark:text-gray-300" data-testid={`hist-totales-${historial.historial_id}`}>
                                🧾 {retentionLabel(historial)}: {Number(historial.retencion_total).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                {' · '}
                                <span className="text-emerald-600 dark:text-emerald-400">USD ${Number(historial.usd_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            {(historial.ids_filtrados?.length > 0 || historial.ids_filtrados_count > 0) && (
                              <button
                                onClick={() => handleDescargarIdsFiltrados(historial.historial_id)}
                                className="text-xs px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition-colors"
                                title="Descargar la semana filtrada (ID + Respuesta)"
                              >
                                🗂️ IDs filtrados
                              </button>
                            )}
                            {historial.archivos?.map((archivo, aIdx) => (
                              <button
                                key={aIdx}
                                onClick={() => handleDescargarBotResultado(historial.historial_id, archivo.union)}
                                className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                              >
                                {archivo.union}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>)}


      {/* Historial de Snapshots PPST */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Historial de Snapshots PPST</h3>
          <button
            onClick={() => { setShowSnapshotHistory(!showSnapshotHistory); if (!showSnapshotHistory && snapshotHistory.length === 0) cargarSnapshotHistory(); }}
            className="px-3 py-2 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm transition-colors"
          >
            {showSnapshotHistory ? 'Ocultar' : 'Ver Historial'}
          </button>
        </div>
        {showSnapshotHistory && (
          <div>
            {loadingSnapshots ? (
              <p className="text-gray-500 text-center py-4">Cargando...</p>
            ) : snapshotHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Sin snapshots</p>
            ) : (
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Fecha</th>
                      <th className="px-3 py-2 text-left font-semibold">Importado</th>
                      <th className="px-3 py-2 text-left font-semibold">Archivo</th>
                      <th className="px-3 py-2 text-center font-semibold">Torneos</th>
                      <th className="px-3 py-2 text-center font-semibold">Recuperados</th>
                      <th className="px-3 py-2 text-center font-semibold">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshotHistory.map((snap, idx) => (
                      <tr key={idx} className={`border-b dark:border-gray-700 ${snap.status === 'recuperado_parcial' ? 'bg-yellow-50 dark:bg-yellow-900/20' : snap.status === 'completo' ? 'bg-green-50 dark:bg-green-900/10' : 'bg-red-50 dark:bg-red-900/10'}`}>
                        <td className="px-3 py-2 font-mono">{snap.fecha}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{snap.fecha_importacion ? snap.fecha_importacion.substring(0, 10) : '-'}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-[200px] truncate" title={snap.archivo}>{snap.archivo || '-'}</td>
                        <td className="px-3 py-2 text-center">{snap.torneos_reales}</td>
                        <td className="px-3 py-2 text-center">{snap.torneos_recuperados > 0 ? snap.torneos_recuperados : '-'}</td>
                        <td className="px-3 py-2 text-center">
                          {snap.status === 'completo' && <span className="px-2 py-1 bg-green-200 text-green-800 rounded-full text-xs font-bold">Completo</span>}
                          {snap.status === 'recuperado_parcial' && <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full text-xs font-bold">Reimportar</span>}
                          {snap.status === 'sin_datos' && <span className="px-2 py-1 bg-red-200 text-red-800 rounded-full text-xs font-bold">Sin datos</span>}
                          {snap.status === 'mixto' && <span className="px-2 py-1 bg-blue-200 text-blue-800 rounded-full text-xs font-bold">Parcial</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-3 flex gap-4 text-xs text-gray-500">
                  <span><span className="inline-block w-3 h-3 bg-green-200 rounded mr-1"></span> Completo: datos originales del Excel</span>
                  <span><span className="inline-block w-3 h-3 bg-yellow-200 rounded mr-1"></span> Reimportar: datos recuperados sin buy-in/GTD</span>
                  <span><span className="inline-block w-3 h-3 bg-red-200 rounded mr-1"></span> Sin datos: snapshot perdido</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Leyenda de última actualización */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-2">
          <RefreshCw className="w-4 h-4 text-violet-600" />
          <h3 className="font-semibold text-violet-900 dark:text-violet-300">Última Actualización</h3>
        </div>
        <LastAutomaticImport />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-green-700 dark:text-green-400 font-medium">💰 PPSR Cash:</span>
              {lastUpdatePPSR ? (
                <>
                  <span className="text-gray-700 dark:text-gray-300">
                    {new Date(lastUpdatePPSR).toLocaleString('es-AR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                  timeZone: 'America/Argentina/Buenos_Aires'
                    })}
                  </span>
                </>
              ) : (
                <span className="text-gray-500 italic">Sin importaciones</span>
              )}
            </div>
            {lastUpdateMethodPPSR && (
              <div className="ml-6 text-xs text-gray-600 dark:text-gray-400">
                🤖 {lastUpdateMethodPPSR}
              </div>
            )}
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-blue-700 dark:text-blue-400 font-medium">🏆 PPST Torneos:</span>
              {lastUpdatePPST ? (
                <span className="text-gray-700 dark:text-gray-300">
                  {new Date(lastUpdatePPST).toLocaleString('es-AR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  timeZone: 'America/Argentina/Buenos_Aires'
                  })}
                </span>
              ) : (
                <span className="text-gray-500 italic">Sin importaciones</span>
              )}
            </div>
            {lastUpdateMethodPPST && (
              <div className="ml-6 text-xs text-gray-600 dark:text-gray-400">
                🤖 {lastUpdateMethodPPST}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabla Total PPSR */}
      {activeTab === 'ppsr' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header con título, filtros y switch de ratio */}
        <div className="bg-slate-800 dark:bg-slate-900 px-4 py-3">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <h3 className="text-lg font-bold text-white">
              Unión 2006 Ratio de fichas 1:6
            </h3>
            
            <div className="flex flex-wrap items-center gap-3">
              {/* Filtros de Fecha */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-white">Desde:</label>
                <input
                  type="date" aria-label="PPSR desde"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 w-[150px] max-w-full px-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-white">Hasta:</label>
                <input
                  type="date" aria-label="PPSR hasta"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 w-[150px] max-w-full px-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900"
                />
              </div>
              
              {/* Switch de Ratio */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowRealValues(!showRealValues)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    showRealValues
                      ? 'bg-white text-green-600 shadow-md'
                      : 'bg-green-700 text-white hover:bg-green-600'
                  }`}
                >
                  1:1 {showRealValues ? '✓' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla con scroll horizontal */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {/* Headers agrupados */}
              <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                <th rowSpan="2" className="px-3 py-3 text-left font-semibold bg-green-50 dark:bg-gray-700 border-r border-gray-300">
                  Nombre de Unión
                </th>
                <th rowSpan="2" className="px-3 py-3 text-center font-semibold bg-green-50 dark:bg-gray-700 border-r border-gray-300">
                  Unión ID
                </th>
                <th rowSpan="2" className="px-3 py-3 text-center font-semibold bg-green-50 dark:bg-gray-700 border-r border-gray-300">
                  Clasificación de PPSR
                </th>
                <th colSpan="4" className="px-3 py-2 text-center font-bold bg-red-100 dark:bg-red-900/30 border-r border-gray-300">
                  Ganancia
                </th>
                <th colSpan="5" className="px-3 py-2 text-center font-bold bg-green-100 dark:bg-green-900/30">
                  Ganancias de la unión
                </th>
              </tr>
              <tr className="border-b border-gray-300 dark:border-gray-600">
                {/* Ganancia (ROJO) */}
                <th className="px-3 py-2 text-center font-semibold text-xs bg-red-50 dark:bg-red-900/20 border-r border-gray-200">Total</th>
                <th className="px-3 py-2 text-center font-semibold text-xs bg-red-50 dark:bg-red-900/20 border-r border-gray-200">De oponentes</th>
                <th className="px-3 py-2 text-center font-semibold text-xs bg-red-50 dark:bg-red-900/20 border-r border-gray-200">Del jackpot</th>
                <th className="px-3 py-2 text-center font-semibold text-xs bg-red-50 dark:bg-red-900/20 border-r border-gray-300">Del dividir EV</th>
                {/* Ganancias de la unión (VERDE) */}
                <th className="px-3 py-2 text-center font-semibold text-xs bg-green-50 dark:bg-green-900/20 border-r border-gray-200">Total</th>
                <th className="px-3 py-2 text-center font-semibold text-xs bg-green-50 dark:bg-green-900/20 border-r border-gray-200">Cuota</th>
                <th className="px-3 py-2 text-center font-semibold text-xs bg-green-50 dark:bg-green-900/20 border-r border-gray-200">Cuota del Jackpot</th>
                <th className="px-3 py-2 text-center font-semibold text-xs bg-green-50 dark:bg-green-900/20 border-r border-gray-200">El premio del Jackpot</th>
                <th className="px-3 py-2 text-center font-semibold text-xs bg-green-50 dark:bg-green-900/20">Dividir EV</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-gray-300">
              {tablaTotalPPSR.map((union, idx) => (
                <tr key={union.union_id} className={`border-b border-gray-200 dark:border-gray-700 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}`}>
                  <td className="px-3 py-2 font-medium">{union.nombre_union}</td>
                  <td className="px-3 py-2 text-center">{union.union_id}</td>
                  <td className="px-3 py-2 text-center">{union.clasificacion_ppsr}</td>
                  {/* Ganancia (ROJO) */}
                  <td className={`px-3 py-2 text-right font-semibold ${union.ganancia_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? union.ganancia_total / 6 : union.ganancia_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-2 text-right ${union.ganancia_de_oponentes >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? union.ganancia_de_oponentes / 6 : union.ganancia_de_oponentes).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-2 text-right ${union.ganancia_del_jackpot >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? union.ganancia_del_jackpot / 6 : union.ganancia_del_jackpot).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-2 text-right ${union.ganancia_del_dividir_ev >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? union.ganancia_del_dividir_ev / 6 : union.ganancia_del_dividir_ev).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  {/* Ganancias de la unión (VERDE) */}
                  <td className={`px-3 py-2 text-right font-semibold ${union.ganancias_union_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? union.ganancias_union_total / 6 : union.ganancias_union_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-2 text-right ${union.ganancias_union_cuota >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? union.ganancias_union_cuota / 6 : union.ganancias_union_cuota).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-2 text-right ${union.ganancias_union_cuota_jackpot >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? union.ganancias_union_cuota_jackpot / 6 : union.ganancias_union_cuota_jackpot).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-2 text-right ${union.ganancias_union_premio_jackpot >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? union.ganancias_union_premio_jackpot / 6 : union.ganancias_union_premio_jackpot).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-2 text-right ${union.ganancias_union_dividir_ev >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? union.ganancias_union_dividir_ev / 6 : union.ganancias_union_dividir_ev).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
              
              {/* Fila Total */}
              {totalPPSRRow && (
                <tr className="bg-yellow-50 dark:bg-yellow-900/20 border-t-2 border-yellow-500 font-bold">
                  <td className="px-3 py-3" colSpan="3">Total</td>
                  {/* Ganancia (ROJO) */}
                  <td className={`px-3 py-3 text-right ${totalPPSRRow.ganancia_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? totalPPSRRow.ganancia_total / 6 : totalPPSRRow.ganancia_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-3 text-right ${totalPPSRRow.ganancia_de_oponentes >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? totalPPSRRow.ganancia_de_oponentes / 6 : totalPPSRRow.ganancia_de_oponentes).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-3 text-right ${totalPPSRRow.ganancia_del_jackpot >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? totalPPSRRow.ganancia_del_jackpot / 6 : totalPPSRRow.ganancia_del_jackpot).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-3 text-right ${totalPPSRRow.ganancia_del_dividir_ev >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? totalPPSRRow.ganancia_del_dividir_ev / 6 : totalPPSRRow.ganancia_del_dividir_ev).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  {/* Ganancias de la unión (VERDE) */}
                  <td className={`px-3 py-3 text-right ${totalPPSRRow.ganancias_union_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? totalPPSRRow.ganancias_union_total / 6 : totalPPSRRow.ganancias_union_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-3 text-right ${totalPPSRRow.ganancias_union_cuota >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? totalPPSRRow.ganancias_union_cuota / 6 : totalPPSRRow.ganancias_union_cuota).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-3 text-right ${totalPPSRRow.ganancias_union_cuota_jackpot >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? totalPPSRRow.ganancias_union_cuota_jackpot / 6 : totalPPSRRow.ganancias_union_cuota_jackpot).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-3 text-right ${totalPPSRRow.ganancias_union_premio_jackpot >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? totalPPSRRow.ganancias_union_premio_jackpot / 6 : totalPPSRRow.ganancias_union_premio_jackpot).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-3 py-3 text-right ${totalPPSRRow.ganancias_union_dividir_ev >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(showRealValues ? totalPPSRRow.ganancias_union_dividir_ev / 6 : totalPPSRRow.ganancias_union_dividir_ev).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {activeTab === 'ppst' && <PPSTOriginalTotals api={API} dateFrom={dateFrom} dateTo={dateTo}
        setDateFrom={setDateFrom} setDateTo={setDateTo} refresh={snapshots} />}

      {/* Progress bar durante upload */}
      {isUploading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{uploadStage}</span>
            <span className="text-sm text-gray-600">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-violet-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Tabs PPSR / PPST / TOTALES */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('totales')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'totales'
              ? 'text-violet-600 border-b-2 border-violet-600'
              : 'text-gray-600 hover:text-gray-900 dark:text-gray-400'
          }`}
        >
          📊 TOTALES
        </button>
        <button
          onClick={() => setActiveTab('ppsr')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'ppsr'
              ? 'text-violet-600 border-b-2 border-violet-600'
              : 'text-gray-600 hover:text-gray-900 dark:text-gray-400'
          }`}
        >
          💰 PPSR (Cash Games)
        </button>
        <button
          onClick={() => setActiveTab('ppst')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'ppst'
              ? 'text-violet-600 border-b-2 border-violet-600'
              : 'text-gray-600 hover:text-gray-900 dark:text-gray-400'
          }`}
        >
          🏆 PPST (Torneos)
        </button>
        <button
          onClick={() => setActiveTab('overlay')}
          data-testid="bancos-tab-overlay"
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'overlay'
              ? 'text-violet-600 border-b-2 border-violet-600'
              : 'text-gray-600 hover:text-gray-900 dark:text-gray-400'
          }`}
        >
          <Calculator className="w-4 h-4 inline mr-1" />
          Overlay Semanal
        </button>
        <button
          onClick={() => setActiveTab('historico')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'historico'
              ? 'text-violet-600 border-b-2 border-violet-600'
              : 'text-gray-600 hover:text-gray-900 dark:text-gray-400'
          }`}
        >
          <History className="w-4 h-4 inline mr-1" />
          Histórico
        </button>
      </div>

      {/* Contenido según tab */}
      {activeTab === 'totales' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-5">
          <h2 className="text-lg font-semibold mb-4">Resumen PPSR + PPST</h2>
          
          {/* Tabla resumen combinada */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Card PPSR */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold mb-4 text-green-700 dark:text-green-400">💰 PPSR (Cash Games)</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-700 dark:text-gray-300">Ganancia Total:</span>
                  <span className={`font-bold ${totalPPSRRow?.ganancia_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {totalPPSRRow ? (showRealValues ? totalPPSRRow.ganancia_total / 6 : totalPPSRRow.ganancia_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 dark:text-gray-300">De oponentes:</span>
                  <span className={`font-semibold ${totalPPSRRow?.ganancia_de_oponentes >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {totalPPSRRow ? (showRealValues ? totalPPSRRow.ganancia_de_oponentes / 6 : totalPPSRRow.ganancia_de_oponentes).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 dark:text-gray-300">Del jackpot:</span>
                  <span className={`font-semibold ${totalPPSRRow?.ganancia_del_jackpot >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {totalPPSRRow ? (showRealValues ? totalPPSRRow.ganancia_del_jackpot / 6 : totalPPSRRow.ganancia_del_jackpot).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </span>
                </div>
                <div className="border-t-2 border-green-300 pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="font-bold text-gray-800 dark:text-gray-200">Ganancias Unión Total:</span>
                    <span className={`font-bold text-lg ${totalPPSRRow?.ganancias_union_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {totalPPSRRow ? (showRealValues ? totalPPSRRow.ganancias_union_total / 6 : totalPPSRRow.ganancias_union_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card PPST */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-400">🏆 PPST (Torneos)</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-700 dark:text-gray-300">Ganancia:</span>
                  <span className={`font-bold ${totalPPSTRow?.ganancia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {totalPPSTRow ? (showRealValues ? totalPPSTRow.ganancia / 6 : totalPPSTRow.ganancia).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 dark:text-gray-300">Valor ganado ticket:</span>
                  <span className={`font-semibold ${totalPPSTRow?.valor_ganado_ticket >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {totalPPSTRow ? (showRealValues ? totalPPSTRow.valor_ganado_ticket / 6 : totalPPSTRow.valor_ganado_ticket).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </span>
                </div>
                <div className="border-t-2 border-blue-300 pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="font-bold text-gray-800 dark:text-gray-200">Ganancias Unión Total:</span>
                    <span className={`font-bold text-lg ${totalPPSTRow?.ganancias_union_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {totalPPSTRow ? (showRealValues ? totalPPSTRow.ganancias_union_total / 6 : totalPPSTRow.ganancias_union_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                    </span>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Déficit Garantizado:</span>
                    <span className={`font-semibold ${totalPPSTRow?.ganancias_union_deficit_garantizado >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {totalPPSTRow ? (showRealValues ? totalPPSTRow.ganancias_union_deficit_garantizado / 6 : totalPPSTRow.ganancias_union_deficit_garantizado).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Total Combinado */}
          <div className="bg-violet-50 dark:bg-violet-900/20 rounded-xl p-4 border border-violet-200 dark:border-violet-800">
            <h3 className="text-base font-semibold mb-4 text-violet-700 dark:text-violet-400">Total combinado</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Ganancia Total PPSR</p>
                <p className={`text-3xl font-bold ${totalPPSRRow?.ganancia_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalPPSRRow ? (showRealValues ? totalPPSRRow.ganancia_total / 6 : totalPPSRRow.ganancia_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Ganancia Total PPST</p>
                <p className={`text-3xl font-bold ${totalPPSTRow?.ganancia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalPPSTRow ? (showRealValues ? totalPPSTRow.ganancia / 6 : totalPPSTRow.ganancia).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">TOTAL GENERAL</p>
                <p className={`text-2xl font-semibold tabular-nums ${((totalPPSRRow?.ganancia_total || 0) + (totalPPSTRow?.ganancia || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(() => {
                    const totalGeneral = (totalPPSRRow?.ganancia_total || 0) + (totalPPSTRow?.ganancia || 0);
                    return (showRealValues ? totalGeneral / 6 : totalGeneral).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  })()}
                </p>
              </div>
            </div>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-violet-200">
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700 dark:text-gray-300">Ganancias Unión PPSR:</span>
                <span className={`font-bold ${totalPPSRRow?.ganancias_union_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalPPSRRow ? (showRealValues ? totalPPSRRow.ganancias_union_total / 6 : totalPPSRRow.ganancias_union_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700 dark:text-gray-300">Ganancias Unión PPST:</span>
                <span className={`font-bold ${totalPPSTRow?.ganancias_union_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalPPSTRow ? (showRealValues ? totalPPSTRow.ganancias_union_total / 6 : totalPPSTRow.ganancias_union_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'overlay' && (
        <OverlayTab />
      )}

      {activeTab === 'historico' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-5">
          <h2 className="text-xl font-bold mb-4">Histórico de Importaciones</h2>
          
          {snapshots.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No hay importaciones todavía</p>
              <p className="text-sm mt-2">Sube tu primer archivo Excel para comenzar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {snapshots.map((snapshot) => (
                <div
                  key={snapshot.snapshot_id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{snapshot.super_union_id}</h3>
                        <span className={`px-2 py-1 text-xs rounded font-semibold ${
                          snapshot.tipo === 'PPST' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {snapshot.tipo === 'PPST' ? '🏆 PPST Torneos' : '💰 PPSR Cash'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Fecha: {snapshot.fecha}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Importado: {new Date(snapshot.fecha_importacion).toLocaleString('es-AR')}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-violet-600">
                        {snapshot.total_ppsr_partidas || snapshot.total_ppst_partidas || 0}
                      </div>
                      <div className="text-xs text-gray-500">partidas totales</div>
                    </div>
                  </div>
                  
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    {snapshot.tipo === 'PPST' && (
                      <div>
                        <span className="text-gray-600">PPST:</span>
                        <span className="font-semibold ml-1">{snapshot.total_ppst_partidas}</span>
                      </div>
                    )}
                    {snapshot.tipo === 'PPSR' && (
                      <div>
                        <span className="text-gray-600">PPSR:</span>
                        <span className="font-semibold ml-1">{snapshot.total_ppsr_partidas}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600">Uniones:</span>
                      <span className="font-semibold ml-1">{snapshot.total_uniones}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Modalidades:</span>
                      <span className="font-semibold ml-1">
                        {snapshot.modalidades_ppsr?.length || snapshot.modalidades_ppst?.length || 0}
                      </span>
                    </div>
                  </div>
                  
                  {((snapshot.modalidades_ppsr && snapshot.modalidades_ppsr.length > 0) || 
                    (snapshot.modalidades_ppst && snapshot.modalidades_ppst.length > 0)) && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {snapshot.modalidades_ppsr && snapshot.modalidades_ppsr.map(mod => (
                        <span key={mod} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                          {mod}
                        </span>
                      ))}
                      {snapshot.modalidades_ppst && snapshot.modalidades_ppst.map(mod => (
                        <span key={mod} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                          {mod}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(activeTab === 'ppsr' || activeTab === 'ppst') && (
        <div className="space-y-4">
          {/* Busqueda Combinada PPSR+PPST */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">
              Busqueda Combinada PPSR + PPST
            </h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {['club', 'union'].map(t => (
                <button key={t} onClick={() => { setTipoBusquedaComb(t); setResultadoComb(null); setSugerencias([]); setMostrarSugerencias(false); }}
                  data-testid={`combinada-tipo-${t}`}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                    tipoBusquedaComb === t ? (t === 'club' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white') : 'bg-gray-100 text-gray-600'
                  }`}>
                  {t === 'club' ? 'ID Club' : 'ID Union'}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <label className="text-xs text-gray-500">Desde</label>
                <input type="date" aria-label="Búsqueda desde" value={fechaDesdeComb} onChange={e => setFechaDesdeComb(e.target.value)}
                  className="block h-9 w-[160px] max-w-full px-2.5 border rounded-lg dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Hasta</label>
                <input type="date" aria-label="Búsqueda hasta" value={fechaHastaComb} onChange={e => setFechaHastaComb(e.target.value)}
                  className="block h-9 w-[160px] max-w-full px-2.5 border rounded-lg dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>
              <div className="relative flex-1 min-w-[220px]">
                {tipoBusquedaComb === 'club' ? (
                  <input type="text" value={busquedaClubComb}
                    onChange={e => { setBusquedaClubComb(e.target.value); fetchSugerencias(e.target.value); }}
                    onFocus={() => { if (busquedaClubComb.length >= 1 && sugerencias.length > 0) setMostrarSugerencias(true); }}
                    onBlur={() => setTimeout(() => setMostrarSugerencias(false), 180)}
                    onKeyDown={e => { if (e.key === 'Enter') buscarCombinado(); if (e.key === 'Escape') setMostrarSugerencias(false); }}
                    placeholder="ID o nombre del club..."
                    data-testid="combinada-input-club"
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
                ) : (
                  <input type="text" value={busquedaUnionComb}
                    onChange={e => { setBusquedaUnionComb(e.target.value); fetchSugerencias(e.target.value); }}
                    onFocus={() => { if (busquedaUnionComb.length >= 1 && sugerencias.length > 0) setMostrarSugerencias(true); }}
                    onBlur={() => setTimeout(() => setMostrarSugerencias(false), 180)}
                    onKeyDown={e => { if (e.key === 'Enter') buscarCombinado(); if (e.key === 'Escape') setMostrarSugerencias(false); }}
                    placeholder="ID o nombre de la unión..."
                    data-testid="combinada-input-union"
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
                )}

                {/* Dropdown de sugerencias */}
                {mostrarSugerencias && (sugerencias.length > 0 || loadingSugerencias) && (
                  <div data-testid="combinada-sugerencias-dropdown"
                    className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                    {loadingSugerencias && (
                      <div className="px-3 py-2 text-xs text-gray-500">Buscando...</div>
                    )}
                    {sugerencias.map((sug, idx) => {
                      const esFuzzy = sug.score !== undefined && sug.score < 1;
                      return (
                        <button
                          key={`${sug.id}-${idx}`}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); seleccionarSugerencia(sug); }}
                          data-testid={`combinada-sugerencia-${sug.id}`}
                          className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                {sug.nombre || `ID ${sug.id}`}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                ID: {sug.id}
                                {sug.total_clubs !== undefined && ` · ${sug.total_clubs} clubs`}
                                {sug.sample_clubs && sug.sample_clubs.length > 0 && ` · ${sug.sample_clubs.join(', ')}`}
                              </div>
                            </div>
                            {esFuzzy && (
                              <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0" title="Coincidencia aproximada">
                                ~{Math.round(sug.score * 100)}%
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {mostrarSugerencias && !loadingSugerencias && sugerencias.length === 0 &&
                 ((tipoBusquedaComb === 'club' ? busquedaClubComb : busquedaUnionComb).length >= 1) && (
                  <div data-testid="combinada-sugerencias-vacio"
                    className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-500">
                    Sin sugerencias — probá con otro término
                  </div>
                )}
              </div>
              <button onClick={() => buscarCombinado()} disabled={loadingComb}
                data-testid="combinada-btn-buscar"
                className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold">
                {loadingComb ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
          </div>

          {/* Resultado Combinado */}
          {resultadoComb && (
            <div className="space-y-4">
              {/* Resumen PPSR vs PPST */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-lg text-gray-900 dark:text-white">
                    {resultadoComb.nombre_club ? `Club: ${resultadoComb.nombre_club}` : `Union: ${resultadoComb.union_id}`}
                    {resultadoComb.total_clubs && ` (${resultadoComb.total_clubs} clubs)`}
                  </h3>
                  <button onClick={exportarCombinado}
                    className="flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 text-sm">
                    <Download className="w-4 h-4" /> Excel Combinado
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left bg-gray-100 dark:bg-gray-700"></th>
                        <th className="px-4 py-2 text-right bg-green-100 text-green-800 font-bold">PPSR</th>
                        <th className="px-4 py-2 text-right bg-orange-100 text-orange-800 font-bold">PPST</th>
                        <th className="px-4 py-2 text-right bg-indigo-100 text-indigo-800 font-bold">TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">Jugadores Activos</td>
                        <td className="px-4 py-2 text-right font-bold text-green-700">{resultadoComb.ppsr?.jugadores_activos}</td>
                        <td className="px-4 py-2 text-right font-bold text-orange-700">{resultadoComb.ppst?.jugadores_activos}</td>
                        <td className="px-4 py-2 text-right font-bold text-indigo-700">{resultadoComb.totales?.jugadores_activos}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">Ganancia</td>
                        <td className={`px-4 py-2 text-right font-bold ${resultadoComb.ppsr?.ganancia_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {resultadoComb.ppsr?.ganancia_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                        </td>
                        <td className={`px-4 py-2 text-right font-bold ${resultadoComb.ppst?.ganancia_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {resultadoComb.ppst?.ganancia_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                        </td>
                        <td className={`px-4 py-2 text-right font-bold ${resultadoComb.totales?.ganancia_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {resultadoComb.totales?.ganancia_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-semibold">Cuota</td>
                        <td className="px-4 py-2 text-right">{resultadoComb.ppsr?.cuota_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}</td>
                        <td className="px-4 py-2 text-right">{resultadoComb.ppst?.cuota_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}</td>
                        <td className="px-4 py-2 text-right font-bold">{resultadoComb.totales?.cuota_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Desglose por club (solo si es union) */}
              {resultadoComb.clubs && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                  <h3 className="font-bold mb-3 text-gray-900 dark:text-white">Desglose por Club</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100 dark:bg-gray-700">
                          <th className="px-3 py-2 text-left">Club</th>
                          <th className="px-3 py-2 text-right bg-green-50 text-green-800">PPSR Jug</th>
                          <th className="px-3 py-2 text-right bg-green-50 text-green-800">PPSR Gan</th>
                          <th className="px-3 py-2 text-right bg-orange-50 text-orange-800">PPST Jug</th>
                          <th className="px-3 py-2 text-right bg-orange-50 text-orange-800">PPST Gan</th>
                          <th className="px-3 py-2 text-right font-bold">Total Jug</th>
                          <th className="px-3 py-2 text-right font-bold">Total Gan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultadoComb.clubs?.map((c, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                            <td className="px-3 py-1.5">
                              <span className="font-semibold">{c.nombre_club}</span>
                              <span className="text-xs text-gray-400 ml-1">({c.club_id})</span>
                            </td>
                            <td className="px-3 py-1.5 text-right text-green-700 font-semibold">{c.ppsr_jugadores_activos}</td>
                            <td className={`px-3 py-1.5 text-right ${c.ppsr_ganancia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {c.ppsr_ganancia?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                            </td>
                            <td className="px-3 py-1.5 text-right text-orange-700 font-semibold">{c.ppst_jugadores_activos}</td>
                            <td className={`px-3 py-1.5 text-right ${c.ppst_ganancia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {c.ppst_ganancia?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                            </td>
                            <td className="px-3 py-1.5 text-right font-bold">{c.total_jugadores}</td>
                            <td className={`px-3 py-1.5 text-right font-bold ${c.total_ganancia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {c.total_ganancia?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tabla jugadores PPSR del club */}
              {resultadoComb.ppsr?.jugadores && !resultadoComb.clubs && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-lg p-4">
                    <h4 className="font-bold text-green-800 mb-2">PPSR - {resultadoComb.ppsr.jugadores.length} jugadores</h4>
                    <div className="overflow-y-auto max-h-80">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0"><tr className="bg-green-100">
                          <th className="px-2 py-1 text-left">ID</th><th className="px-2 py-1 text-left">Nombre</th>
                          <th className="px-2 py-1 text-right">Mesas</th><th className="px-2 py-1 text-right">Ganancia</th>
                        </tr></thead>
                        <tbody>
                          {resultadoComb.ppsr.jugadores.slice(0, 50).map((j, i) => (
                            <tr key={i} className={i%2===0?'':'bg-green-50'}>
                              <td className="px-2 py-0.5 font-mono">{j.jugador_id}</td>
                              <td className="px-2 py-0.5">{j.jugador_nombre}</td>
                              <td className="px-2 py-0.5 text-right">{j.mesas}</td>
                              <td className={`px-2 py-0.5 text-right font-semibold ${j.ganancia>=0?'text-green-600':'text-red-600'}`}>{j.ganancia?.toLocaleString('es-AR',{minimumFractionDigits:2})}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-bold text-orange-800 mb-2">PPST - {resultadoComb.ppst.jugadores.length} jugadores</h4>
                    <div className="overflow-y-auto max-h-80">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0"><tr className="bg-orange-100">
                          <th className="px-2 py-1 text-left">ID</th><th className="px-2 py-1 text-left">Apodo</th>
                          <th className="px-2 py-1 text-right">Torneos</th><th className="px-2 py-1 text-right">Ganancia</th>
                        </tr></thead>
                        <tbody>
                          {resultadoComb.ppst.jugadores.slice(0, 50).map((j, i) => (
                            <tr key={i} className={i%2===0?'':'bg-orange-50'}>
                              <td className="px-2 py-0.5 font-mono">{j.jugador_id}</td>
                              <td className="px-2 py-0.5">{j.apodo}</td>
                              <td className="px-2 py-0.5 text-right">{j.torneos}</td>
                              <td className={`px-2 py-0.5 text-right font-semibold ${j.ganancia>=0?'text-green-600':'text-red-600'}`}>{j.ganancia?.toLocaleString('es-AR',{minimumFractionDigits:2})}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
